import { bookingList, bulkBookingUpdate } from "../services/booking";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { unSettledInvoices } from "../services/invoice";
import { getBookedRooms } from "../utils/nightAuditHelper";
import { message } from "antd";
import { createPayment } from "../services/payment";

export const getBookingListForNightAudit = createAsyncThunk(
  "getBookingListForNightAudit",
  async (
    payload,
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    const data = {
      statuses: payload.statuses,
      hotelId: payload.hotelId,

      pageNumber: payload.pageNumber,
      pageSize: payload.pageSize,
    };

    const state = getState();
    const allRooms = state?.rooms?.response; // Replace `otherSlice` with the actual slice name

    try {
      const response = await bookingList({ ...payload, ...data }, "bookings");
      return fulfillWithValue({ data: response.data, allRooms: allRooms });
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("token");
      }

      throw rejectWithValue(error.message);
    }
  }
);

export const getUnSettlledInvoiceListForNightAudit = createAsyncThunk(
  "getUnSettlledInvoiceListForNightAudit",
  async (
    payload,
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await unSettledInvoices(payload);
      return fulfillWithValue(response.data);
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("token");
      }

      throw rejectWithValue(error.message);
    }
  }
);

export const updateMultipleBookingForNightAudit = createAsyncThunk(
  "updateMultipleBookingForNightAudit",
  async (
    payload,
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const response = await bulkBookingUpdate(payload, "bookings/update");

      let updatedBookingId = response?.data?.map((Obj) => Obj?.id);

      let status = payload?.[0]?.status;

      if (updatedBookingId?.length <= 0 && status === "checkedIn") {
        message.info(
          `Cannot check in as the rooms from this booking 
          are already occupied by ongoing bookings. Please check out first.`
        );
      }

      return fulfillWithValue(updatedBookingId);
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("token");
      }

      throw rejectWithValue(error.message);
    }
  }
);

export const postPaymentForNightAudit = createAsyncThunk(
  "postPaymentForNightAudit",
  async (
    payload,
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const paymentResponse = await createPayment(payload?.paymentPayload);

      await dispatch(
        getUnSettlledInvoiceListForNightAudit(payload?.invoicePayload)
      );

      return fulfillWithValue(paymentResponse);
    } catch (error) {
      if (error?.response?.status == 401) {
        localStorage.removeItem("token");
      }

      throw rejectWithValue(error.message);
    }
  }
);

const initialState = {
  loading: false,
  response: [],
  apiError: null,
  displayNightAuditDrawer: false,

  pageNumber: 0,
  pageSize: 20,
  infinateScroll: false,
  infinateScrollLoading: false,

  bulkUpdateLoading: false,
  bulkUpdateError: false,

  showTakePaymentDrawer: false,
  createPaymentApiLoading: false,
  createPaymentApiError: false,

  showAttachToPartyDrawer: false,
  attachPartyApiLoading: false,
  attachPartyApiError: false,

  attachPartyString: "",
  attachPartyValue: "",

  dataChanged: false,
  showNightAuditRoomDarwer: false,
};

export const nightAuditSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    updateDisplayNightAuditDrawer: (state, action) => {
      state.displayNightAuditDrawer = Boolean(action.payload);
    },
    updatePageNo: (state, action) => {
      state.pageNumber = +state.pageNumber + 1;
    },
    resetState: (state, action) => {
      state.infinateScroll = false;
      state.response = [];
      state.pageNumber = 0;
      state.showTakePaymentDrawer = false;
    },
    resetPageNo: (state, action) => {
      state.pageNumber = 0;
    },
    updateInfinitieScroll: (state, action) => {
      state.infinateScroll = action.payload;
    },

    updateShowTakePaymentDrawer: (state, action) => {
      state.showTakePaymentDrawer = !state.showTakePaymentDrawer;
    },
    updateShowAttachToPartyDrawer: (state, action) => {
      state.showAttachToPartyDrawer = action.payload;
    },
    updateAttachPartyString: (state, action) => {
      state.attachPartyString = action.payload;
    },
    updateAttachPartyValue: (state, action) => {
      state.attachPartyValue = action.payload;
    },
    updateShowNightAuditDrawer: (state, action) => {
      state.showNightAuditRoomDarwer = action.payload;
    },
    updateDataChanged: (state, action) => {
      state.dataChanged = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getBookingListForNightAudit.pending, (state, action) => {
      state.loading = state.infinateScroll ? false : true;
      state.apiError = false;
    });
    builder.addCase(getBookingListForNightAudit.fulfilled, (state, action) => {
      state.loading = false;

      const data = action.payload?.data;
      const allRooms = action?.payload?.allRooms;

      // Initialize data with defaults
      const bookingList = data?.bookings ?? [];
      const invoiceList = data?.invoices ?? [];
      const paymentsList = data?.payments ?? [];

      // Set infinite scroll state
      const hasBookings = bookingList.length > 0;
      state.infinateScroll = hasBookings;
      state.infinateScrollLoading = hasBookings;

      // Create lookups for invoices and payments
      const invoiceLookup = invoiceList?.reduce((acc, invoice) => {
        if (!acc[invoice.bookingId]) acc[invoice.bookingId] = [];
        acc[invoice.bookingId].push(invoice);
        return acc;
      }, {});

      const paymentLookup = paymentsList?.reduce((acc, payment) => {
        if (!acc[payment.invoiceId]) acc[payment.invoiceId] = [];
        acc[payment.invoiceId].push(payment);
        return acc;
      }, {});

      // Process booking data
      const newData = bookingList?.map((booking) => {
        const currentInvoices = invoiceLookup[booking.id] || [];
        const roomInvoice = currentInvoices.find(
          (invoice) => invoice.invoiceType === "roomBill"
        );

        const currentPayments = paymentLookup[roomInvoice?.id] || [];

        return {
          ...booking,
          total: currentInvoices.reduce(
            (sum, invoice) => sum + (invoice?.total || 0),
            0
          ),
          paid: currentPayments.reduce(
            (sum, payment) => sum + (payment?.amount || 0),
            0
          ),
          updatedRooms: getBookedRooms(allRooms, booking?.rooms),
        };
      });

      // Ensure state.response is always an array
      state.response = Array.isArray(state.response) ? state.response : [];

      // Append new data to the existing state.response
      state.response = [...state.response, ...newData];

      state.apiError = false;
    });
    builder.addCase(getBookingListForNightAudit.rejected, (state, action) => {
      state.infinateScrollLoading = false;
      state.loading = false;
      state.response = [];
      state.apiError = true;
    });

    builder.addCase(
      getUnSettlledInvoiceListForNightAudit.pending,
      (state, action) => {
        state.loading = state.infinateScroll ? false : true;
        state.apiError = false;
      }
    );
    builder.addCase(
      getUnSettlledInvoiceListForNightAudit.fulfilled,
      (state, action) => {
        state.loading = false;

        const hasData = action?.payload?.length > 0;
        state.infinateScroll = hasData;
        state.infinateScrollLoading = hasData;

        // Ensure state.response is always an array
        state.response = Array.isArray(state.response) ? state.response : [];

        // Append new data to the existing state.response
        state.response = [...state.response, ...action?.payload];

        state.apiError = false;
      }
    );

    builder.addCase(
      getUnSettlledInvoiceListForNightAudit.rejected,
      (state, action) => {
        state.infinateScrollLoading = false;
        state.loading = false;
        state.response = [];
        state.apiError = true;
      }
    );

    builder.addCase(
      updateMultipleBookingForNightAudit.pending,
      (state, action) => {
        state.bulkUpdateLoading = true;
        state.bulkUpdateError = false;
      }
    );
    builder.addCase(
      updateMultipleBookingForNightAudit.fulfilled,
      (state, action) => {
        state.loading = false;

        let removedBookingId = action?.payload;

        // Ensure state.response is always an array
        // state.response = Array.isArray(state.response) ? state.response : [];

        let currentState = Array.isArray(state.response) ? state.response : [];

        let updatedData = currentState?.filter(
          (Obj) => !removedBookingId?.includes(Obj?.id)
        );

        state.response = updatedData;
        state.bulkUpdateLoading = false;
        state.apiError = false;
      }
    );

    builder.addCase(
      updateMultipleBookingForNightAudit.rejected,
      (state, action) => {
        state.bulkUpdateLoading = false;
        state.response = [];
        state.bulkUpdateError = true;
      }
    );

    builder.addCase(postPaymentForNightAudit.pending, (state, action) => {
      state.createPaymentApiLoading = true;
      state.createPaymentApiError = false;

      state.response = [];
      state.loading = true;

      state.infinateScroll = false;
    });
    builder.addCase(postPaymentForNightAudit.fulfilled, (state, action) => {
      state.loading = false;

      // let removedBookingId = action?.payload;

      // // Ensure state.response is always an array
      // // state.response = Array.isArray(state.response) ? state.response : [];

      // let currentState = Array.isArray(state.response) ? state.response : [];

      // let updatedData = currentState?.filter(
      //   (Obj) => !removedBookingId?.includes(Obj?.id)
      // );

      // state.response = updatedData;

      message.success("Payment successfull");
      state.createPaymentApiLoading = false;
      state.createPaymentApiError = false;
    });

    builder.addCase(postPaymentForNightAudit.rejected, (state, action) => {
      state.createPaymentApiLoading = false;
      state.createPaymentApiError = true;

      state.loading = false;
      state.apiError = true;
      message.info("Something went wrong");
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  updateDisplayNightAuditDrawer,
  updatePageNo,
  resetPageNo,
  resetState,
  updateInfinitieScroll,
  updateShowTakePaymentDrawer,

  updateShowAttachToPartyDrawer,
  updateAttachPartyString,
  updateAttachPartyValue,

  updateShowNightAuditDrawer,
  updateDataChanged,
} = nightAuditSlice.actions;

export default nightAuditSlice.reducer;
